/**
 * CSV import with a mandatory preview.
 *
 * The dry run runs the real import inside a transaction and rolls back, so
 * the preview is exactly what would happen -- not a guess. Half-finished
 * imports leaving a chart in an unknown state are how master data gets
 * quietly corrupted.
 */
import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { importLedgers } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { ImportResult } from '../api/types'
import { c } from '../theme'

export default function ImportDialog(
  { onClose, onDone }: { onClose: () => void; onDone: () => void },
) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function run(dryRun: boolean) {
    if (!file) return
    setBusy(true); setError(null)
    try {
      const r = await importLedgers(file, dryRun)
      if (dryRun) setPreview(r)
      else { onDone(); onClose() }
    } catch (e) { setError(errorMessage(e)) } finally { setBusy(false) }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t('chart.importTitle')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('chart.importColumns')}
        </Typography>
        <Box component="pre" sx={{
          fontFamily: 'var(--mono)', fontSize: 11.5, bgcolor: c.surfacePage,
          border: `1px solid ${c.border}`, borderRadius: 1, p: 1.5, m: 0,
          overflowX: 'auto',
        }}>
{`code,name_en,name_ar,group_code,opening_balance,opening_dr_cr
D0001,Al Rajhi Trading,مؤسسة الراجحي,DEBTORS,1500.00,D`}
        </Box>

        <Button component="label" variant="outlined" sx={{ mt: 2 }}>
          {file ? file.name : t('chart.chooseFile')}
          <input type="file" accept=".csv,text/csv" hidden
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null) }} />
        </Button>

        {preview && (
          <Stack spacing={1.5} sx={{ mt: 2.5 }}>
            <Alert severity={preview.rejected ? 'warning' : 'success'}>
              {t('chart.previewSummary', {
                created: preview.created, rejected: preview.rejected })}
            </Alert>
            {preview.errors.length > 0 && (
              <Box className="ruled" sx={{
                maxHeight: 180, overflowY: 'auto',
                border: `1px solid ${c.border}`, borderRadius: 1,
              }}>
                {preview.errors.map((e, i) => (
                  <Stack key={i} direction="row" spacing={1} sx={{ px: 1.5, py: 0.75 }}>
                    <span className="num" style={{ fontSize: 11.5, color: c.inkMuted,
                      minWidth: 56, textAlign: 'start' }}>
                      {t('chart.row')} {e.row}</span>
                    <Typography sx={{ fontSize: 12.5, color: c.dangerInk }}>
                      {e.reason}</Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={() => run(true)} disabled={!file || busy}>
          {t('chart.preview')}
        </Button>
        <Button variant="contained" disabled={!preview || !preview.created || busy}
          onClick={() => run(false)}>
          {t('chart.applyImport', { n: preview?.created ?? 0 })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
